import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RestoreIcon from "@mui/icons-material/Restore";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import axios from "axios";
import { TextField } from "../ui";
import { tokens } from "../../theme";

interface WhatsAppMessageDialogProps {
  open: boolean;
  onClose: () => void;
  clientName: string;
  clientPhone: string;
  topic: string;
  initialMessage: string;
  templateStorageKey: string;
  staffName?: string;
  onSendBusiness?: (message: string) => Promise<void>;
  sendingBusiness?: boolean;
}

const DEFAULT_TEMPLATE =
  "Hi [Client Name], this is [Your Name] from Muzukuru. I am contacting you regarding [Topic].";

const normalizePhone = (phone: string) => {
  let digits = phone.replace(/\D/g, "");
  if (digits.startsWith("0")) digits = `263${digits.slice(1)}`;
  return digits;
};

export default function WhatsAppMessageDialog({
  open,
  onClose,
  clientName,
  clientPhone,
  topic,
  initialMessage,
  templateStorageKey,
  staffName = "Muzukuru",
  onSendBusiness,
  sendingBusiness = false,
}: WhatsAppMessageDialogProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [message, setMessage] = useState(initialMessage);
  const [originalMessage, setOriginalMessage] = useState(initialMessage);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGeneratedText, setAiGeneratedText] = useState("");
  const [aiError, setAiError] = useState("");

  const compileTemplate = useCallback(
    (value: string) =>
      value
        .replace(/\[Client Name\]/g, clientName.trim().split(/\s+/)[0] || "there")
        .replace(/\[Your Name\]/g, staffName)
        .replace(/\[Topic\]/g, topic),
    [clientName, staffName, topic],
  );

  useEffect(() => {
    if (!open) return;
    const storedTemplate = localStorage.getItem(templateStorageKey);
    const startingMessage = storedTemplate
      ? compileTemplate(storedTemplate)
      : initialMessage;
    setTemplate(storedTemplate || DEFAULT_TEMPLATE);
    setMessage(startingMessage);
    setOriginalMessage(startingMessage);
    setShowTemplateEditor(false);
    setAiPanelOpen(false);
    setAiPrompt("");
    setAiGeneratedText("");
    setAiError("");
  }, [open, initialMessage, templateStorageKey, compileTemplate]);

  const openWhatsApp = () => {
    const phone = normalizePhone(clientPhone);
    if (phone) {
      window.open(
        `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
    onClose();
  };

  const generateWithAI = async (instruction = aiPrompt) => {
    setAiError("");
    setAiLoading(true);
    const apiKey =
      process.env.REACT_APP_GEMINI_API_KEY ||
      process.env.GEMINI_API_KEY ||
      (window as unknown as { GEMINI_API_KEY?: string }).GEMINI_API_KEY;
    if (!apiKey) {
      setAiError("AI writing is unavailable because its API key is not configured.");
      setAiLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
        {
          contents: [
            {
              parts: [
                {
                  text: [
                    "Write a clear, professional and empathetic WhatsApp message for Muzukuru staff.",
                    `Client: ${clientName}`,
                    `Topic: ${topic}`,
                    "Current draft:",
                    message,
                    `Instruction: ${instruction || "Improve the current draft while preserving its meaning."}`,
                    "Return only the message text.",
                  ].join("\n"),
                },
              ],
            },
          ],
        },
      );
      const generated = response.data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (generated) setAiGeneratedText(generated.trim().replace(/^```[a-zA-Z]*\n?|\n?```$/g, ""));
      else setAiError("Could not generate a message. Please try again.");
    } catch (error: unknown) {
      setAiError(axios.isAxiosError(error)
        ? error.response?.data?.error?.message || error.message
        : error instanceof Error ? error.message : "Could not generate a message.");
    } finally {
      setAiLoading(false);
    }
  };

  const saveTemplate = (value: string) => {
    setTemplate(value);
    setMessage(compileTemplate(value));
  };

  const saveTemplateAndOpen = () => {
    localStorage.setItem(templateStorageKey, template);
    openWhatsApp();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>Send WhatsApp Message</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Prepare a message for <strong>{clientName}</strong> ({clientPhone}).
        </Typography>
        <TextField
          label="Message Content"
          multiline
          rows={4}
          fullWidth
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
          }}
        >
          <Button
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setAiPanelOpen((value) => !value)}
            sx={{
              bgcolor: colors.primary[400],
              color: theme.palette.getContrastText(colors.primary[400]),
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": { bgcolor: colors.primary[500] },
            }}
          >
            {aiPanelOpen ? "Close AI Assistant" : "Write with AI"}
          </Button>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {message !== originalMessage && (
              <Button
                size="small"
                startIcon={<RestoreIcon />}
                onClick={() => setMessage(originalMessage)}
                sx={{ textTransform: "none", color: "text.secondary" }}
              >
                Reset to Original
              </Button>
            )}
          </Box>
        </Box>

        <Collapse in={aiPanelOpen} timeout="auto" unmountOnExit>
          <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "action.hover" }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Muzukuru AI Assistant</Typography>
            <TextField
              label="What should the AI change?"
              placeholder="For example, make it warmer or more concise"
              multiline
              rows={2}
              fullWidth
              value={aiPrompt}
              onChange={(event) => setAiPrompt(event.target.value)}
              sx={{ mb: 1.5 }}
              disabled={aiLoading}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {[
                ["Professional", "Make this message more professional and polite."],
                ["Empathetic", "Make this message warm and empathetic."],
                ["Short & Direct", "Make this message concise and direct."],
              ].map(([label, instruction]) => (
                <Chip
                  key={label}
                  label={label}
                  size="small"
                  clickable
                  disabled={aiLoading}
                  onClick={() => {
                    setAiPrompt(instruction);
                    void generateWithAI(instruction);
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button
                onClick={() => void generateWithAI()}
                variant="contained"
                disabled={aiLoading}
                startIcon={
                  aiLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AutoAwesomeIcon />
                  )
                }
                sx={{
                  bgcolor: colors.primary[400],
                  textTransform: "none",
                  borderRadius: 2,
                  "&:hover": { bgcolor: colors.primary[500] },
                }}
              >
                {aiLoading ? "Generating..." : "Generate with AI"}
              </Button>
            </Box>
            {aiError && <Alert severity="error" sx={{ mb: 1 }}>{aiError}</Alert>}
            {aiGeneratedText && (
              <Box sx={{ p: 1.5, bgcolor: "background.paper", borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" mb={1}>AI Suggestion</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1.5 }}>{aiGeneratedText}</Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setAiGeneratedText("")}
                    sx={{ textTransform: "none" }}
                  >
                    Discard
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      setMessage(aiGeneratedText);
                      setAiGeneratedText("");
                      setAiPanelOpen(false);
                    }}
                    sx={{
                      bgcolor: "#25D366",
                      color: "white",
                      textTransform: "none",
                      "&:hover": { bgcolor: "#128C7E" },
                    }}
                  >
                    Apply Message
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>

        <Box sx={{ mb: 1 }}>
          <Button
            size="small"
            onClick={() => setShowTemplateEditor((value) => !value)}
            sx={{ textTransform: "none", color: colors.primary[700] }}
          >
            {showTemplateEditor ? "Hide Template Settings" : "Configure Default Template"}
          </Button>
        </Box>
        {showTemplateEditor && (
          <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>Default Message Template</Typography>
            <TextField
              label="Template String"
              multiline
              rows={3}
              fullWidth
              value={template}
              onChange={(event) => saveTemplate(event.target.value)}
              helperText="Use [Client Name], [Your Name], and [Topic]"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap" }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        {onSendBusiness && (
          <Button
            onClick={() => {
              localStorage.setItem(templateStorageKey, template);
              void onSendBusiness(message);
            }}
            variant="contained"
            disabled={sendingBusiness || !normalizePhone(clientPhone)}
            startIcon={
              sendingBusiness ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <WhatsAppIcon />
              )
            }
            sx={{ textTransform: "none" }}
          >
            {sendingBusiness ? "Sending" : "Send via Muzukuru Business"}
          </Button>
        )}
        <Button
          onClick={saveTemplateAndOpen}
          variant="contained"
          disabled={!normalizePhone(clientPhone)}
          startIcon={<WhatsAppIcon />}
          sx={{
            bgcolor: "#25D366",
            color: "#fff",
            textTransform: "none",
            "&:hover": { bgcolor: "#128C7E" },
          }}
        >
          Open WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
}
